import { useEffect } from "react";

/**
 * Acquire the Screen Wake Lock so the kiosk tablet's screen never sleeps.
 * Falls back silently if the browser does not support the API.
 * Re-acquires the lock when the page comes back into view.
 */
export default function useWakeLock(enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return undefined;

    let lock = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled) return;
      try {
        // eslint-disable-next-line no-undef
        lock = await navigator.wakeLock.request("screen");
        lock.addEventListener("release", () => { lock = null; });
      } catch {
        /* User may have denied permission or device unsupported — ignore. */
      }
    };

    acquire();

    const onVisible = () => {
      if (document.visibilityState === "visible" && lock === null) acquire();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      try { lock?.release?.(); } catch { /* ignore */ }
    };
  }, [enabled]);
}
